import React, { useEffect, useState } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { getPrograms } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { formatCategoryName } from '../utils/formatCategory';

interface ProgramItem {
  id: string;
  title: string;
  category: string;
  description: string;
  icon: 'Legal' | 'Education' | 'Shelter';
  colorTheme: 'purple' | 'green' | 'yellow';
}

const Programs: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<ProgramItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 8;

  useEffect(() => {
    const fetchPrograms = async () => {
      if (page === 1) setLoading(true);
      try {
        const { data, meta } = await getPrograms(locale, page, pageSize);
        const formatted = data.map((item: any) => ({
          id: item.documentId || item.id,
          title: item.title,
          category: item.category || 'Program',
          description: item.description,
          icon: item.icon || 'Legal',
          colorTheme: item.colorTheme || 'purple'
        }));
        
        if (page === 1) {
          setPrograms(formatted);
        } else {
          setPrograms(prev => [...prev, ...formatted]);
        }

        if (meta && meta.pagination) {
          setHasMore(page < meta.pagination.pageCount);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Failed to fetch programs", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [locale, page]);

  const navigateToProgram = (id: string) => {
    navigate(`/${locale}/programlar/${id}`);
  };

  const getColorClasses = (theme: string) => {
    switch (theme) {
      case 'green':
        return {
          bg: 'bg-brand-green-50',
          border: 'border-brand-green-100',
          shadow: 'shadow-brand-green-900/5',
          text: 'text-brand-green-700',
          iconBg: 'bg-white',
          btnBg: 'bg-brand-green-700',
          btnHover: 'hover:bg-brand-green-900'
        };
      case 'yellow':
        return {
          bg: 'bg-white',
          border: 'border-brand-yellow-100',
          shadow: 'shadow-brand-yellow-900/5',
          text: 'text-brand-yellow-600',
          iconBg: 'bg-brand-yellow-50',
          btnBg: 'bg-brand-yellow-500',
          btnHover: 'hover:bg-brand-yellow-600'
        };
      case 'purple':
      default:
        return {
          bg: 'bg-white',
          border: 'border-brand-purple-50',
          shadow: 'shadow-brand-purple-900/5',
          text: 'text-brand-purple-600',
          iconBg: 'bg-brand-purple-50',
          btnBg: 'bg-brand-purple-900',
          btnHover: 'hover:bg-brand-purple-700'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-neutral-bg">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-brand-purple-900"></div>
      </div>
    );
  }

  return (
    <div className="py-24 animate-in fade-in slide-in-from-left-4 duration-700">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20 space-y-6">
          <div className="inline-flex items-center justify-center space-x-2 text-brand-green-500 font-bold text-xs uppercase tracking-widest w-full">
            <span className="w-8 h-px bg-brand-green-500"></span>
            <span>{t('programs.subtitle')}</span>
            <span className="w-8 h-px bg-brand-green-500"></span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-brand-purple-900 italic leading-tight">{t('programs.title')}</h1>
          <p className="text-xl md:text-2xl text-gray-500 font-light leading-relaxed max-w-3xl mx-auto">
            {t('programs.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-12">
          {programs.length > 0 ? programs.map((prog, index) => {
            const colors = getColorClasses(prog.colorTheme);
            const IconComp = ICONS[prog.icon as keyof typeof ICONS] || ICONS.Legal;
            
            return (
              <div key={prog.id} className={`${colors.bg} rounded-[3rem] p-10 shadow-xl ${colors.shadow} border ${colors.border} flex flex-col h-full justify-between gap-8 group transition-all hover:-translate-y-2`}>
                <div className="flex-1 space-y-6">
                  <div className={`flex items-center space-x-4 ${colors.text}`}>
                    <div className={`w-14 h-14 ${colors.iconBg} rounded-2xl flex items-center justify-center shadow-sm`}>
                      <IconComp className="w-7 h-7" />
                    </div>
                    <span className="font-bold tracking-widest uppercase text-sm">{formatCategoryName(prog.category, t)}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-purple-900 leading-tight group-hover:text-brand-purple-600 transition-colors">
                    {prog.title}
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed font-light">
                    {prog.description}
                  </p>
                </div>
                <div className="shrink-0 mt-4">
                  <button 
                    onClick={() => navigateToProgram(prog.id)}
                    className={`${colors.btnBg} text-white px-8 py-4 rounded-2xl font-bold ${colors.btnHover} transition-all shadow-lg hover:-translate-y-1 w-full flex items-center justify-center whitespace-nowrap`}
                  >
                    {t('programs.details')}
                    <svg className="w-5 h-5 ml-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </button>
                </div>
              </div>
            );
          }) : (
            <div className="text-center py-20 text-gray-400 font-light">
              {t('programs.empty')}
            </div>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-16 flex justify-center">
            <button
              onClick={() => setPage(p => p + 1)}
              className="bg-white border-2 border-brand-purple-200 text-brand-purple-900 font-bold px-10 py-4 rounded-2xl shadow-sm hover:shadow-lg hover:border-brand-purple-500 hover:text-brand-purple-600 transition-all active:scale-95"
            >
              {t('resources.load_more')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Programs;
